import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ThemeColors {
  dark: 'dark' | false;
  light: 'light' | false;
  backgroundColor: string;
  textColor: string;
  stepBackgroundColor?: string;
  grayScale400?: string;
  inputBg: string;
  grayScale200?: string;
  iconColor: string;
  bColor?: string;
  inputBackground: string;
  grayScale600?: string;
  bgColor: string;
  transparent: string;
  // Common colors
  alertColor?: string;
  white?: string;
  primary: string;
  primary2?: string;
  gradient1?: string;
  gradient2?: string;
  green: string;
  textColor1?: string;
  placeHolderColor?: string;
  borderColor: string;
  grayScale60?: string;
  primary50?: string;
  grayScale700?: string;
  iconBackgroundColor?: string;
  modalBackground?: string;
  grayScale100?: string;
  grayScale500?: string;
  gradientBackground?: string;
  blue?: string;
  black?: string;
  primary4?: string;
  green1?: string;
}

const LightColor: ThemeColors = {
  light: 'light',
  dark: false,
  backgroundColor: '#FFFFFF',
  textColor: '#171717',
  stepBackgroundColor: '#F1F5F9',
  grayScale400: '#A3A3A3',
  inputBg: '#e2e1df',
  grayScale200: '#E5E5E5',
  iconColor: '#171717',
  bColor: '#EEEEEE',
  inputBackground: '#F8F9FD',
  grayScale600: '#525252',
  bgColor: '#EDF0F4',
  transparent: 'rgba(240, 240, 240, 0.32)',
  // Common colors
  alertColor: '#E82C81',
  white: '#FFFFFF',
  primary: '#6B39F4',
  primary2: '#B59CFA',
  gradient1: 'rgba(107,57,244,1)',
  gradient2: 'rgba(159,3,242,1)',
  green: '#1DCE5C',
  textColor1: '#525252',
  placeHolderColor: '#9E9E9E',
  borderColor: '#35383F',
  grayScale60: '#F8F5FF',
  primary50: '#F8F5FF',
  grayScale700: '#404040',
  iconBackgroundColor: '#ECEEFE',
  modalBackground: 'rgba(118,118,118,0.7)',
  grayScale100: '#F5F5F5',
  grayScale500: '#64748B',
  gradientBackground: 'rgba(255,255,255,0.2)',
  blue: '#0153FF',
  black: '#0F172A',
  primary4: '#7A4DF5',
  green1: '#E9FCEF',
};

const DarkColor: ThemeColors = {
  dark: 'dark',
  light: false,
  backgroundColor: '#20202C',
  stepBackgroundColor: '#334155',
  textColor: '#FAFAFA',
  inputBg: '#404040',
  iconColor: '#FAFAFA',
  bColor: '#35383F',
  inputBackground: '#1E293B',
  bgColor: '#EDF0F4',
  transparent: 'rgba(15,23,42,0.8)',
  // Common colors (same as light)
  alertColor: '#E82C81',
  white: '#FFFFFF',
  primary: '#6B39F4',
  primary2: '#B59CFA',
  gradient1: 'rgba(107,57,244,1)',
  gradient2: 'rgba(159,3,242,1)',
  green: '#1DCE5C',
  textColor1: '#525252',
  placeHolderColor: '#9E9E9E',
  borderColor: '#35383F',
  grayScale60: '#F8F5FF',
  primary50: '#F8F5FF',
  grayScale700: '#334155',
  iconBackgroundColor: '#ECEEFE',
  modalBackground: 'rgba(118,118,118,0.7)',
  grayScale100: '#F1F5F9',
  grayScale500: '#64748B',
  grayScale400: '#94A3B8',
  gradientBackground: 'rgba(255,255,255,0.2)',
  blue: '#0153FF',
  black: '#0F172A',
  grayScale600: '#475569',
  primary4: '#7A4DF5',
  green1: '#E9FCEF',
};

interface ThemeState {
  theme: ThemeColors;
  isDark: boolean;
}

const initialState: ThemeState = {
  theme: LightColor,
  isDark: false,
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    toggleTheme: (state) => {
      state.isDark = !state.isDark;
      state.theme = state.isDark ? DarkColor : LightColor;

      // Update document class for Tailwind dark mode
      if (typeof document !== 'undefined') {
        if (state.isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.isDark = action.payload === 'dark';
      state.theme = state.isDark ? DarkColor : LightColor;

      // Update document class for Tailwind dark mode
      if (typeof document !== 'undefined') {
        if (state.isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    },
  },
});

export const { toggleTheme, setTheme } = themeSlice.actions;
export default themeSlice.reducer;
